import React from 'react';
import { Building2, Target, Shield, Heart, Award, Users } from 'lucide-react';
import { useApp } from '../context/AppContext';

const WAREHOUSE_BG = 'https://images.unsplash.com/photo-1672552226650-796f40198c47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBzaGVsdmVzJTIwZGFya3xlbnwwfHx8fDE3NzI0NzY4NTJ8MA&ixlib=rb-4.1.0&q=85';

export const AboutPage = () => {
  const { t } = useApp();

  const values = [
    { icon: Award, title: t('about.values.quality'), color: '#10B981' },
    { icon: Shield, title: t('about.values.trust'), color: '#00D4FF' },
    { icon: Target, title: t('about.values.value'), color: '#F59E0B' },
    { icon: Heart, title: t('about.values.service'), color: '#EF4444' },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="about-page">
      {/* Hero */}
      <section 
        className="py-32 relative"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.95), rgba(5,5,5,0.7)), url(${WAREHOUSE_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D4FF]/10 text-[#00D4FF] text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              Since 2004
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="about-title">
              {t('about.title')}
            </h1>
            <p className="text-xl text-white/60">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text mb-2">20+</div>
              <div className="text-white/50">{t('home.yearsExperience')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text mb-2">50K+</div>
              <div className="text-white/50">{t('home.unitsSold')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text mb-2">15+</div>
              <div className="text-white/50">{t('about.countriesServed')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text mb-2">500+</div>
              <div className="text-white/50">{t('about.businessClients')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">{t('about.mission')}</h2>
            <p className="text-xl text-white/70 leading-relaxed">
              {t('about.missionText')}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-16">{t('about.values.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <div 
                key={idx}
                className="card-dark p-8 text-center group hover:neon-glow transition-all duration-300"
                data-testid={`value-card-${idx}`}
              >
                <div 
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: `${value.color}20`, color: value.color }}
                >
                  <value.icon className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-semibold">{value.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">{t('about.ourStory')}</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>{t('about.ourStoryP1')}</p>
                <p>{t('about.ourStoryP2')}</p>
              </div>
            </div>
            <div className="card-dark p-8">
              <h3 className="text-xl font-semibold mb-6 gradient-text">{t('about.whatSetsUsApart')}</h3>
              <ul className="space-y-4">
                {(t('about.apartItems') || []).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#00D4FF] rounded-full mt-2" />
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Team CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Users className="w-12 h-12 text-[#00D4FF] mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">{t('about.workWithUs')}</h2>
          <p className="text-white/60 mb-8">
            {t('about.workWithUsText')}
          </p>
          <a href="/contact" className="btn-primary">
            {t('about.contactOurTeam')}
          </a>
        </div>
      </section>
    </div>
  );
};
